import React, { useState, useEffect } from "react";
import { styled, useTheme } from "@mui/material/styles";
import {
  AppBar as MuiAppBar,
  Box,
  Button,
  CssBaseline,
  Drawer as MuiDrawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  InputBase,
  Divider,
  Avatar,
  Typography,
  Tooltip,
  Skeleton,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import newLogo from "../../static/newLogo.png";
import MenuIcon from "@mui/icons-material/Menu";
import SearchIcon from "@mui/icons-material/Search";
import NavMenu from "../../Components/NavMenu";
import Footer from "../../Components/Footer";
import UpdateProfile from "../Dashboard/Profile/UpdateProfile.jsx";
import SchoolSection from "./School/SchoolSection";
import PlansSection from "./Plans/PlansSection";
import GradesSection from "./Grades/GradesSection";
// import Sections from "./Section/Sections";
import SubjectSection from "./Subject/SubjectSection";
import SessionSection from "./AcademicSession/SessionSection";
import TimeIntervalSection from "./TimeInterval/TimeIntervalSection";
import TeachersSection from "./Teachers/TeachersSection";
import ClassesSection from "./Classes/ClassesSection";
import StudentsSection from "./Students/StudentsSection";
import UploadImage from "./UploadImage/UploadImage.jsx";
import HolidaysSection from "./Holidays/HolidaysSection.jsx";
import HomeData from "./HomeData/HomeData.jsx";
import {
  getSchoolInfo,
  getSchoolLogo,
  isAuthenticated,
} from "../../ApiService/ApiService";
import { LogoutSharp } from "@mui/icons-material";
import FeesSection from "./Fees/FeesSection.jsx";
import ExpenseSection from "./Expense/ExpenseSection.jsx";
import CustomDialog from "../../Components/CustomDialog.jsx";
import ExpenseCategorySection from "./ExpenseCategory/ExpenseCategorySection.jsx";

const drawerWidth = 240;

const openedMixin = (theme) => ({
  backgroundColor: "#f5f5f5",
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  backgroundColor: "#f5f5f5",
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  backgroundColor: "#2659c8",
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",

  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

export default function SideBar({ sideNavOptions }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const userProficPic = sessionStorage.getItem("profileImage");
  const schoolId = sessionStorage.getItem("schoolId");
  const [open, setOpen] = useState(true);
  const [selectedOption, setSelectedOption] = useState("DashBoard");
  const [schoolInfo, setSchoolInfo] = useState({});
  const [schoolLogo, setSchoolLogo] = useState("");
  const [dialog, setDialog] = useState({
    visible: false,
    title: "",
    message: "",
    buttons: [],
  });

  const handleDialogClose = () => {
    setDialog({ ...dialog, visible: false });
  };

  const fetchSchoolLogo = async (schoolId) => {
    const response = await getSchoolLogo(schoolId);
    setSchoolLogo(response?.data?.data?.url);
  };
  const fetchSchoolInfo = async (schoolId) => {
    const response = await getSchoolInfo(schoolId);
    setSchoolInfo(response?.data?.data);
  };

  useEffect(() => {
    fetchSchoolLogo(schoolId);
    fetchSchoolInfo(schoolId);
  }, [schoolId]);

  const handleLogout = () => {
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("role");
    sessionStorage.removeItem("schoolId");
    sessionStorage.removeItem("firstTimeLogin");
    sessionStorage.removeItem("profileImage");
    sessionStorage.removeItem("userId");
    setDialog({
      visible: true,
      title: "Success",
      message: "Logged Out!",
      buttons: [{ name: "OK", onPress: handleDialogClose }],
    });
    navigate("/login");
  };

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleOptionSelect = (option) => {
    setSelectedOption(option);
  };

  let mainContent;
  switch (selectedOption) {
    case "DashBoard":
      mainContent = <HomeData handleLogout={handleLogout} />;
      break;
    case "Profile":
      mainContent = <UpdateProfile handleLogout={handleLogout} />;
      break;
    case "Schools":
      mainContent = <SchoolSection />;
      break;
    case "Plans":
      mainContent = <PlansSection />;
      break;
    case "Subjects":
      mainContent = <SubjectSection />;
      break;
    case "Grades":
      mainContent = <GradesSection />;
      break;
    case "Academic Sessions":
      mainContent = <SessionSection />;
      break;
    case "Time Intervals":
      mainContent = <TimeIntervalSection />;
      break;
    case "Teachers":
      mainContent = <TeachersSection />;
      break;
    case "Classes":
      mainContent = <ClassesSection />;
      break;
    case "Students":
      mainContent = <StudentsSection />;
      break;
    case "Fees":
      mainContent = <FeesSection />;
      break;
    case "Expense Category":
      mainContent = <ExpenseCategorySection />;
      break;
    case "Expense":
      mainContent = <ExpenseSection />;
      break;
    case "Holidays":
      mainContent = <HolidaysSection />;
      break;
    case "School Logo":
      mainContent = (
        <UploadImage
          fetchSchoolLogo={fetchSchoolLogo}
          schoolLogo={schoolLogo}
        />
      );
      break;

    default:
      mainContent = <div>Please select an option from side menu</div>;
  }

  return (
    <>
      <CustomDialog
        visible={dialog.visible}
        title={dialog.title}
        message={dialog.message}
        buttons={dialog.buttons}
        onClose={handleDialogClose}
      />{" "}
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <AppBar position="fixed" open={open}>
          <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
            <IconButton
              color="inherit"
              aria-label={open ? "close drawer" : "open drawer"}
              onClick={open ? handleDrawerClose : handleDrawerOpen}
              edge="start"
            >
              <MenuIcon />
            </IconButton>

            <Box sx={{ display: "flex", alignItems: "center" }}>
              <InputBase
                placeholder="Search..."
                inputProps={{ "aria-label": "search" }}
                sx={{
                  mr: 1,
                  p: 0.5,
                  pl: 1,
                  color: "white",
                  fontSize: "small",
                  border: 1,
                  borderRadius: 3,
                  borderColor: "white",
                  backgroundColor: "#1976D2",
                }}
                endAdornment={
                  <IconButton>
                    <SearchIcon fontSize="small" sx={{ color: "white" }} />
                  </IconButton>
                }
              />

              <Avatar
                src={userProficPic}
                alt="Profile"
                sx={{
                  height: 40,
                  width: 40,
                  border: "1px solid gray",
                  marginLeft: 1,
                  marginRight: 1,
                }}
              />
            </Box>
          </Toolbar>
        </AppBar>

        <Drawer variant="permanent" open={open}>
          <Link to="/">
            <Avatar
              src={isAuthenticated() ? schoolLogo : newLogo}
              alt="Logo"
              sx={{
                height: 55,
                width: 55,
                border: "1px solid gray",
                marginLeft: "auto",
                marginRight: "auto",
                marginTop: 1,
              }}
            />
          </Link>

          <Box
            sx={{
              display: open ? "flex" : "none",
              flexDirection: "column",
              marginBottom: 1,
              padding: 1,
              alignItems: "center",
            }}
          >
            {schoolInfo !== undefined && schoolInfo !== null ? (
              <>
                <Typography fontWeight={400} sx={{ fontSize: 16 }}>
                  {schoolInfo.name || ""}
                </Typography>
                <Typography
                  fontWeight={600}
                  variant="h4"
                  sx={{ fontSize: 12, marginLeft: "30px" }}
                >
                  {schoolInfo.tagLine || ""}
                </Typography>
                {schoolInfo.address !== undefined &&
                  schoolInfo.address !== null && (
                    <>
                      <Typography
                        fontWeight={300}
                        variant="subtitle1"
                        sx={{
                          fontSize: 10,
                          color: "black",
                          marginTop: 2,
                          textAlign: "center",
                        }}
                      >
                        {schoolInfo.address.street1 || ""}
                      </Typography>
                      {schoolInfo.address.street2 !== undefined &&
                        schoolInfo.address.street2 !== null && (
                          <Typography
                            fontWeight={300}
                            variant="subtitle1"
                            sx={{
                              fontSize: 10,
                              color: "black",
                              textAlign: "center",
                            }}
                          >
                            {schoolInfo.address.street2}
                          </Typography>
                        )}
                      {schoolInfo.address.city !== undefined &&
                        schoolInfo.address.state !== undefined &&
                        schoolInfo.address.pin !== undefined && (
                          <Typography
                            fontWeight={300}
                            variant="subtitle1"
                            sx={{
                              fontSize: 10,
                              color: "black",
                              textAlign: "center",
                            }}
                          >
                            {`${schoolInfo.address.city}, ${schoolInfo.address.state}, ${schoolInfo.address.pin}`}
                          </Typography>
                        )}
                    </>
                  )}
              </>
            ) : (
              <>
                <Skeleton
                  variant="text"
                  width={200}
                  height={20}
                  animation="wave"
                />
                <Skeleton
                  variant="text"
                  width={150}
                  height={16}
                  animation="wave"
                />
                <Skeleton
                  variant="text"
                  width={200}
                  height={16}
                  animation="wave"
                />
                <Skeleton
                  variant="text"
                  width={150}
                  height={16}
                  animation="wave"
                />
                <Skeleton
                  variant="text"
                  width={200}
                  height={16}
                  animation="wave"
                />
              </>
            )}
          </Box>

          <Divider />
          <List sx={{ height: 450, overflowX: open ? "auto" : "hidden" }}>
            {sideNavOptions.map((option) => (
              <ListItem
                key={option.text}
                disablePadding
                sx={{
                  display: "block",
                  backgroundColor:
                    selectedOption === option.text ? "#e0e0e0" : "transparent",
                }}
              >
                <ListItemButton
                  onClick={() => handleOptionSelect(option.text)}
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? "initial" : "center",
                    px: 2.5,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : "auto",
                      justifyContent: "center",
                    }}
                  >
                    {option.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={option.text}
                    sx={{ opacity: open ? 1 : 0 }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          {open ? (
            <>
              <Divider />
              <Button
                variant="outlined"
                onClick={handleLogout}
                color="error"
                sx={{ width: "75%", margin: "auto", textTransform: "unset" }}
              >
                Logout <LogoutSharp sx={{ marginLeft: 1 }} fontSize="small" />
              </Button>
            </>
          ) : (
            <>
              <Divider />
              <Tooltip title="Logout">
                {" "}
                <IconButton
                  onClick={handleLogout}
                  sx={{ marginTop: 1, marginLeft: 1 }}
                >
                  <LogoutSharp color="error" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Drawer>

        <Box
          component="main"
          sx={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            minHeight: "100vh",
            position: "relative",
          }}
        >
          <Box sx={{ display: "relative", padding: 3 }}>
            <DrawerHeader />
            {mainContent}
          </Box>

          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              width: "100%",
              backgroundColor: "#e5e5e5",
            }}
          >
            <Footer />
          </Box>
        </Box>
      </Box>
    </>
  );
}
